import {Button, Group, Paper, Stack, Text, Title} from '@mantine/core'
import {IconBriefcase} from '@tabler/icons-react'
import {useEffect, useState} from 'react'

import type {CustomerDataLayer} from './customer-data-layer'
import {CustomerSummary} from './customer-summary'
import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {StepCustomer} from './step-customer'
import type {Customer, JobDetail} from './types'

type JobCustomerSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    customerDataLayer: CustomerDataLayer
    onJobUpdated: (job: JobDetail) => void
}

export function JobCustomerSection({
    job,
    jobDataLayer,
    customerDataLayer,
    onJobUpdated,
}: JobCustomerSectionProps) {
    const {canEdit} = useEditRights()
    const [isEditing, setIsEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [customerId, setCustomerId] = useState(job.customerId)
    const [customerName, setCustomerName] = useState(job.customerName)
    const [customer, setCustomer] = useState<Customer | undefined>(() =>
        customerDataLayer.getCustomerById(job.customerId),
    )

    useEffect(() => {
        const cached = customerDataLayer.getCustomerById(job.customerId)
        if (cached) {
            setCustomer(cached)
            return
        }

        let cancelled = false

        void customerDataLayer.listCustomers({name: job.customerName}).then((results) => {
            if (cancelled) {
                return
            }
            setCustomer(results.find((item) => item.id === job.customerId))
        })

        return () => {
            cancelled = true
        }
    }, [customerDataLayer, job.customerId, job.customerName])

    function startEditing() {
        setCustomerId(job.customerId)
        setCustomerName(job.customerName)
        setErrorMessage(null)
        setIsEditing(true)
    }

    function handleCancel() {
        setIsEditing(false)
        setErrorMessage(null)
    }

    async function handleSave() {
        if (!customerId) {
            setErrorMessage('Customer is required')
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.patchJob(job.id, {
                jobName: job.name,
                serviceTypeId: job.serviceTypeId,
                notes: job.notes,
                customerId,
            })
            onJobUpdated(updatedJob)
            setIsEditing(false)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save customer')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group gap="xs" align="center">
                        <IconBriefcase size={18} stroke={1.5} />
                        <Title order={3}>Customer</Title>
                    </Group>
                    {!isEditing ? (
                        canEdit ? (
                            <Button variant="subtle" size="compact-sm" onClick={startEditing}>
                                Edit
                            </Button>
                        ) : null
                    ) : (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>

                {errorMessage && (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                )}

                {isEditing ? (
                    <Stack gap="sm">
                        <StepCustomer
                            customerId={customerId}
                            error={errorMessage ?? undefined}
                            dataLayer={customerDataLayer}
                            onChange={(nextCustomerId, nextCustomerName) => {
                                setCustomerId(nextCustomerId)
                                setCustomerName(nextCustomerName)
                            }}
                        />
                        {customerName && (
                            <Text size="sm" c="dimmed">
                                Selected: {customerName}
                            </Text>
                        )}
                    </Stack>
                ) : (
                    <CustomerSummary
                        customer={
                            customer ?? {
                                id: job.customerId,
                                name: job.customerName,
                                contactName: '',
                                phone: '',
                            }
                        }
                    />
                )}
            </Stack>
        </Paper>
    )
}
